import { z } from "zod";

/**
 * Back-office roles and the permission matrix — the single source of truth for every guard,
 * menu, button and (in the API pass) every `/admin/*` route check.
 *
 * Sources, merged (decisions in README « Rôles et permissions »):
 *  - Admin-Parametres-Equipe (147d): the 4 staff roles and the per-space M / R / – table (base of the matrix);
 *  - AG-Matrice (AG9) + AG-Roles-Reference: admin-only actions inside a creation dossier, the Lecteur's reads,
 *    checklist / président callbacks, business-rule versions, and the technical roles CTO · DEV · RSSI;
 *  - AF-* : finance core for ADMIN + AGENT, subscriptions / monthly billing / grids / client billing account ADMIN only;
 *  - AC-Matrice: COMPTABLE works only on the companies attached to them (scope "attached", filtered by the API).
 *
 * Refused = 403 `forbidden` « Droits insuffisants pour cette action ».
 */
export const StaffRole = z.enum(["ADMIN", "AGENT", "LECTEUR", "COMPTABLE", "CTO", "DEV", "RSSI"]);
export type StaffRole = z.infer<typeof StaffRole>;

export const ROLE_LABEL: Record<StaffRole, string> = {
  ADMIN: "Administrateur",
  AGENT: "Agent",
  LECTEUR: "Lecteur",
  COMPTABLE: "Comptable",
  CTO: "CTO",
  DEV: "Développeur",
  RSSI: "RSSI",
};

/** Admin-Parametres-Equipe « Créer un accès » descriptions (tech roles: AG-Roles-Reference). */
export const ROLE_DESCRIPTION: Record<StaffRole, string> = {
  ADMIN: "Tout l'espace, paramètres et équipe compris. Code par e-mail à chaque connexion.",
  AGENT: "Traite les dossiers de création d'entreprise.",
  LECTEUR: "Consulte sans rien modifier.",
  COMPTABLE: "Suit les clients en comptabilité et leurs déclarations.",
  CTO: "Console technique complète ; actions d'écriture tracées, jamais de données bancaires en clair.",
  DEV: "Console et journaux en lecture seule ; aucune écriture en production, aucun export de données client.",
  RSSI: "Sécurité et audit (ISO 27001) : journal en lecture, exports pseudonymisés, verrouillage de compte tracé.",
};

/** Avatar gradients and role chips as drawn (AG-Roles-Reference, AG-Equipe, shell user block). */
export const ROLE_STYLE: Record<StaffRole, { avatar: string; chipBg: string; chipText: string }> = {
  ADMIN: { avatar: "linear-gradient(135deg,#6D5CF0,#3B82F6)", chipBg: "#EEF0FE", chipText: "#4B45CC" },
  AGENT: { avatar: "linear-gradient(135deg,#0EA5E9,#22C55E)", chipBg: "#DCFCE7", chipText: "#166534" },
  LECTEUR: { avatar: "linear-gradient(135deg,#A855F7,#EC4899)", chipBg: "#EEF0FE", chipText: "#3E38B0" },
  COMPTABLE: { avatar: "linear-gradient(135deg,#F59E0B,#EF4444)", chipBg: "#FEF3E0", chipText: "#92400E" },
  CTO: { avatar: "linear-gradient(135deg,#1B1C31,#3F4353)", chipBg: "#F1F2F6", chipText: "#1B1C31" },
  DEV: { avatar: "linear-gradient(135deg,#8A8EA3,#B7BCCB)", chipBg: "#F1F2F6", chipText: "#3F4353" },
  RSSI: { avatar: "linear-gradient(135deg,#B42318,#F59E0B)", chipBg: "#FDEAEA", chipText: "#B42318" },
};

/** Only ADMIN gets the e-mail code at sign-in (Admin-Login, Admin-Login-Code; flag `admin_otp_required`). */
export const ROLES_WITH_EMAIL_CODE: readonly StaffRole[] = ["ADMIN"];

/**
 * Capabilities = rows of the matrix. Keep them coarse (one per space / sensitive action family);
 * screens ask `can(session, "finance", "write")`, never `role === "ADMIN"`.
 */
export const Capability = z.enum([
  // ── Création (BO-*, AG-*) ──
  "creation", // dossiers de création SASU / EURL / AE: lists, fiche, actions on assigned dossiers
  "creation.adminActions", // révéler le NIR, rejeter (Melrys), forcer une transition, config support, apports — « Réservé à l'administrateur »
  "creation.followup", // checklist, rappels président, demandes d'informations (AG4, AG7)
  "creation.rules", // versions des règles métier (AG7): publish = write
  // ── Compta (Admin-*, AC-*) ──
  "cabinet.dashboard", // Tableau de bord du cabinet (Admin-Dashboard, AC-Cockpit)
  "cabinet.clients", // clients, fiche client, onboarding comptable
  "cabinet.prospects", // prospects (besoin comptable)
  "cabinet.operations", // transactions, impôts et taxes, documents, social
  "cabinet.access", // rattachements expert-comptable, affectation de l'expert principal (« plus tard »)
  // ── Shared follow-up ──
  "discussions", // fils client (création + compta), messagerie unifiée
  "bookings", // rendez-vous et disponibilités
  // ── Finance (AF-*) ──
  "finance", // vue, paiements, bloqués, remboursements (émettre), rapprochement, factures émises
  "finance.admin", // abonnements, compta mensuelle, grilles, compte de facturation client, indicateurs plateforme
  // ── Plateforme ──
  "saas", // Facturation SaaS (FS-*)
  "platform", // interrupteurs de lancement (platform.features), produit
  "team", // Équipe et rôles (créer un accès, verrouiller, code)
  "settings.firm", // messages et motifs, forfait, intégrations (paramètres du cabinet)
  "settings.personal", // profil, sécurité, 2FA, sessions, préférences de notification
  // ── Technique (TD-*) ──
  "tech.console", // TD1: santé, jobs, webhooks, drapeaux
  "tech.developers", // TD2: intégrations, clés API, webhooks sortants
  "tech.security", // TD3: audit, verrouillage, exports pseudonymisés
  // ── Reference pages (role matrix, state machines) ──
  "reference",
]);
export type Capability = z.infer<typeof Capability>;

/** none < read < write. "write" implies "read". */
export const Level = z.enum(["none", "read", "write"]);
export type Level = z.infer<typeof Level>;

/**
 * Where a role's access applies. "all" = every record; "attached" = companies attached to the user
 * (COMPTABLE, AC-Matrice scope s); the API filters, the UI only labels (« Périmètre : 4 entreprises »).
 */
export type Scope = "all" | "attached";

type Row = Record<StaffRole, Level>;
const row = (ADMIN: Level, AGENT: Level, LECTEUR: Level, COMPTABLE: Level, CTO: Level = "none", DEV: Level = "none", RSSI: Level = "none"): Row => ({
  ADMIN,
  AGENT,
  LECTEUR,
  COMPTABLE,
  CTO,
  DEV,
  RSSI,
});

/** The matrix. Columns: ADMIN · AGENT · LECTEUR · COMPTABLE · CTO · DEV · RSSI. */
export const PERMISSIONS: Record<Capability, Row> = {
  creation: row("write", "write", "read", "read"),
  "creation.adminActions": row("write", "none", "none", "none"),
  "creation.followup": row("write", "write", "none", "none"),
  "creation.rules": row("write", "read", "read", "none"), // AGENT reads the versions (AG-Rappels tab), publishing stays admin-only (AG-Matrice)
  "cabinet.dashboard": row("write", "none", "none", "write"),
  "cabinet.clients": row("write", "none", "none", "write"),
  "cabinet.prospects": row("write", "none", "none", "none"),
  "cabinet.operations": row("write", "none", "none", "write"),
  "cabinet.access": row("write", "none", "none", "none"),
  discussions: row("write", "write", "read", "write"),
  bookings: row("write", "write", "none", "read"),
  finance: row("write", "write", "none", "none"),
  "finance.admin": row("write", "none", "none", "none"),
  saas: row("write", "none", "none", "none"),
  platform: row("write", "none", "none", "none"),
  team: row("write", "none", "none", "none"),
  "settings.firm": row("write", "none", "none", "none"),
  "settings.personal": row("write", "write", "write", "write", "write", "write", "write"),
  "tech.console": row("write", "none", "none", "none", "write", "read", "none"),
  "tech.developers": row("write", "none", "none", "none", "write", "read", "none"),
  "tech.security": row("write", "none", "none", "none", "read", "none", "write"),
  reference: row("read", "read", "read", "read", "read", "read", "read"),
};

/** Record-level scope per role (only COMPTABLE is narrowed today). */
export const ROLE_SCOPE: Record<StaffRole, Scope> = { ADMIN: "all", AGENT: "all", LECTEUR: "all", COMPTABLE: "attached", CTO: "all", DEV: "all", RSSI: "all" };

const RANK: Record<Level, number> = { none: 0, read: 1, write: 2 };

export function levelOf(role: StaffRole, cap: Capability): Level {
  return PERMISSIONS[cap][role];
}

/** True when `role` has at least `mode` on `cap`. Pure: usable by the API's route guards as well. */
export function roleCan(role: StaffRole, cap: Capability, mode: "read" | "write" = "read"): boolean {
  return RANK[levelOf(role, cap)] >= RANK[mode];
}

export const FORBIDDEN_MESSAGE = "Droits insuffisants pour cette action.";
export const ADMIN_ONLY_HINT = "Réservé à l'administrateur";
/** Permanent, non-closable banner for read-only roles (AG-Lecteur, BO drawer). */
export const READ_ONLY_BANNER = "Lecture seule — vous consultez sans pouvoir modifier.";
